import { InputHTMLAttributes, useMemo } from "react";
import { Field as FormikField, FieldProps } from "formik";
import ReactPhoneInput from "react-phone-number-input";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";

import { createPhoneInputComponent } from "./base-input";

import { clsxMerge } from "shared/lib/helpers";
import { CountrySelect } from "shared/ui/user-input/phone-input/country-select";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import "react-phone-number-input/style.css";

type InputPropsI = InputHTMLAttributes<HTMLInputElement>;

interface PhoneInputPropsI extends PropsWithClassNameI {
  name: string;
  label?: string;
  labelClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  errors?: string;
  touched?: boolean;
  inputProps?: InputPropsI;
  inputClassName?: string;
  countrySelectClassName?: string;
  countrySelectMenuClassName?: string;
  isFormikField?: boolean;
  onChange?: (value: string) => void; // only used for non-formik fields, eg. if isFormikField == false
}

export const PhoneInput = ({
  className,
  name,
  label,
  labelClassName,
  placeholder = "Phone number",
  disabled = false,
  errors,
  touched,
  inputProps = {},
  inputClassName = "",
  countrySelectClassName = "",
  countrySelectMenuClassName = "",
  isFormikField,
  onChange = () => {},
}: PhoneInputPropsI) => {
  const CustomizedInputComponent = useMemo(
    () => createPhoneInputComponent(inputClassName),
    [inputClassName]
  );

  return (
    <div className={clsxMerge("form-control mb-3 w-full", className)}>
      {!!label && (
        <div className="mb-2">
          <label
            className={clsxMerge("text-base-content/60", labelClassName, {
              "ae-typography-detail1": !labelClassName?.includes("typography"),
            })}
          >
            {label}
          </label>
        </div>
      )}

      {isFormikField ? (
        <FormikField name={name}>
          {({ field, form }: FieldProps) => {
            return (
              <ReactPhoneInput
                placeholder={placeholder}
                countrySelectComponent={(props) => (
                  <CountrySelect
                    {...props}
                    triggerClassName={countrySelectClassName}
                    menuClassName={countrySelectMenuClassName}
                  />
                )}
                inputComponent={CustomizedInputComponent}
                defaultCountry="US"
                international
                withCountryCallingCode
                countryCallingCodeEditable={false}
                {...field}
                {...inputProps}
                onChange={(value) => {
                  form.setFieldValue(name, value || "");
                }}
                disabled={disabled}
              />
            );
          }}
        </FormikField>
      ) : (
        <ReactPhoneInput
          placeholder={placeholder}
          countrySelectComponent={(props) => (
            <CountrySelect
              {...props}
              triggerClassName={countrySelectClassName}
              menuClassName={countrySelectMenuClassName}
            />
          )}
          inputComponent={CustomizedInputComponent}
          // Match the Formik branch above: without defaultCountry,
          // react-phone-number-input has no country to render and the
          // flag selector stays blank. US is the org-wide default
          // (matches the Formik branch's hardcoded value).
          defaultCountry="US"
          international
          withCountryCallingCode
          countryCallingCodeEditable={false}
          {...inputProps}
          value={(inputProps.value as string) || ""}
          onChange={(value) => onChange(value || "")}
          disabled={disabled}
        />
      )}

      {errors && touched && (
        <div className="mt-1 flex items-center text-xs">
          <ExclamationCircleIcon className="text-error-content bg-neutral-content mr-1 h-5 w-5" />
          <div className="text-error-content">{errors}</div>
        </div>
      )}
    </div>
  );
};
